import { AccountCircleOutlined } from '@mui/icons-material'
import {
  Alert,
  Button,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  Paper,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import Logo from 'common/components/Logo'
import { selectPortal } from 'features/portal/portalSlice'
import i18n from 'lang/idiomas'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import ThemeManager from 'theming/ThemeManager'
import PasswordController from '../lib/PasswordController'

const RecoveryPasswordForm = () => {
  const themeManager = new ThemeManager()

  const navigate = useNavigate()
  const userRef = useRef()

  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState(false)
  const [username, setUsername] = useState('')
  const [responseStatus, setResponseStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const portal = useSelector(selectPortal)

  const logoOutside = themeManager.options.loginLogoOutside ?? false

  const onReCaptchaChange = (value) => {
    setCaptcha(true)
  }

  useEffect(() => {
    userRef.current.focus()
  }, [])

  return (
    <Box
      className="recover-password-container"
      sx={{
        width: {
          xs: '100%',
          md: '60%',
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {logoOutside ? (
        <Logo
          logoType="Large"
          sx={{
            mb: 5,
            transfor: 'translateX(-30%)',
          }}
        />
      ) : (
        <></>
      )}

      <>
        <Logo
          logoType="Large"
          sx={{
            m: {
              xs: 'auto',
              md: '10px 0px 50px 0px',
            },
            my: 5,
            width: {
              xs: '70%',
              md: '30%',
            },
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontWeight: '100',
            color: 'primary.main',
            mt: { xs: 2, md: 4 },
            mb: { xs: 3, md: 5 },
            textAlign: { xs: 'center', md: 'unset' },
            fontSize: {
              xs: '1.75rem',
            },
          }}
        >
          {portal?.portal?.descripcion ?? 'Portal del Empleado'}
        </Typography>
      </>

      <Paper
        className="inicio"
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          p: { xs: 2, sm: 3, md: 4 },
          width: { md: '100%' },
        }}
      >
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h6" sx={{ m: 2 }} align="center">
            {i18n.t('login.titusuar', { defaultValue: 'REESTABLECER CONTRASEÑA' })}
          </Typography>
        </div>

        <Box className="recover-password-form">
          <Paper sx={{ backgroundColor: 'background.paper', p: 3 }}>
            <div>
              <FormControl
                sx={{ width: '100%', minWidth: { xs: '25ch', md: '600px' } }}
                variant="standard"
              >
                <Input
                  error={responseStatus === 404}
                  id="standard-adornment-password"
                  type="text"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton aria-label="icono usuario">
                        <AccountCircleOutlined
                          sx={{ opacity: !username ? 0.7 : 1, transition: '.3s' }}
                          color={responseStatus === 404 ? 'error' : 'primary.text'}
                        />
                      </IconButton>
                    </InputAdornment>
                  }
                  autoComplete="false"
                  inputProps={{
                    placeholder: 'Usuario',
                  }}
                  ref={userRef}
                  value={username}
                  onChange={(e) => {
                    if (responseStatus === 404) setResponseStatus(null)
                    setUsername(e.target.value)
                  }}
                />
                {portal?.portal?.parametros_seo?.recaptcha_key ? (
                  <ReCAPTCHA
                    style={{ marginTop: '8px' }}
                    sitekey={portal.portal.parametros_seo.recaptcha_key}
                    onChange={onReCaptchaChange}
                  />
                ) : (
                  <></>
                )}
              </FormControl>
            </div>
            <div>
              <Button
                onClick={async () => {
                  try {
                    if (portal?.portal?.parametros_seo?.recaptcha_key && !captcha) {
                      enqueueSnackbar('Tienes que aceptar no ser un robot. Gracias', {
                        variant: 'error',
                      })
                      return false
                    }
                    setLoading(true)
                    const response = await PasswordController.requestPasswordReset(username)
                    enqueueSnackbar(response?.data?.mensaje ?? response?.data, {
                      variant: response?.data?.resultado === 'ERROR' ? 'error' : 'success',
                      autoHideDuration: 10000,
                    })

                    navigate('/login', {
                      state: {
                        recovery: false,
                      },
                    })
                  } catch (error) {
                    setResponseStatus(error.response.status)
                    enqueueSnackbar(error.message, { variant: 'error' })
                  } finally {
                    setLoading(false)
                  }
                }}
                disabled={!username || loading}
                variant="contained"
                sx={{ width: '100%', mt: 2 }}
              >
                Enviar
              </Button>
            </div>
            <div>
              <Button sx={{ width: '100%', mt: 2 }} LinkComponent={Link} to="/login">
                Regresar
              </Button>
            </div>
          </Paper>
        </Box>
        {errorMessage ? (
          <Alert
            sx={{ mt: 2, minWidth: '25ch', width: '100%' }}
            variant="outlined"
            severity="error"
          >
            {errorMessage}
          </Alert>
        ) : (
          <></>
        )}
      </Paper>
    </Box>
  )
}

export default RecoveryPasswordForm
